import { useState, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { Eye, Pencil, Trash2, X, Link2, Check } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  useJournalEntry, useJournalLinks, useJournalBucketLinks,
  useUpdateJournalEntry, useDeleteJournalEntry,
  useAddJournalLink, useRemoveJournalLink, useAddJournalBucketLink, useRemoveJournalBucketLink,
} from '@/hooks/useJournal'
import { useBuckets } from '@/hooks/useBuckets'
import { useAllActivePriorities } from '@/hooks/usePriorities'
import { getPromptsForType, type Prompt } from '@/lib/prompts'
import type { JournalEntryType, JournalLink } from '@/types'

const AUTOSAVE_MS = 800

// ─── Link chip ────────────────────────────────────────────────────────────────

function LinkChip({ label, color, onRemove }: { label: string; color?: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] bg-muted rounded-full pl-2 pr-1 py-0.5 max-w-[160px]">
      {color && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />}
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-muted-foreground hover:text-foreground flex-shrink-0"
        aria-label={`Remove link to ${label}`}
      >
        <X size={10} />
      </button>
    </span>
  )
}

// ─── Link selector popover ────────────────────────────────────────────────────

function LinkSelector({
  journalId,
  links,
  bucketLinks,
}: {
  journalId: string
  links: JournalLink[]
  bucketLinks: import('@/types').JournalBucketLink[]
}) {
  const [open, setOpen] = useState(false)
  const buckets = useBuckets()
  const priorities = useAllActivePriorities()
  const addJournalLink = useAddJournalLink()
  const removeJournalLink = useRemoveJournalLink()
  const addJournalBucketLink = useAddJournalBucketLink()
  const removeJournalBucketLink = useRemoveJournalBucketLink()

  const isPriorityLinked = (priorityId: string) => links.some((l) => l.priorityId === priorityId)
  const isBucketLinked = (bucketId: string) => bucketLinks.some((l) => l.bucketId === bucketId)

  async function togglePriority(priorityId: string) {
    if (isPriorityLinked(priorityId)) await removeJournalLink.mutateAsync({ journalId, priorityId })
    else await addJournalLink.mutateAsync({ journalId, priorityId })
  }

  async function toggleBucket(bucketId: string) {
    if (isBucketLinked(bucketId)) await removeJournalBucketLink.mutateAsync({ journalId, bucketId })
    else await addJournalBucketLink.mutateAsync({ journalId, bucketId })
  }

  const activePriorities = priorities ?? []
  const userBuckets = (buckets ?? []).filter((b) => !b.isSystem && !b.archivedAt)

  if (activePriorities.length === 0 && userBuckets.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 gap-1 text-[11px] text-muted-foreground px-2 flex-shrink-0">
          <Link2 size={11} />
          Link
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1.5" align="start" side="top">
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {userBuckets.length > 0 && (
            <>
              <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">
                Life Areas
              </p>
              {userBuckets.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => void toggleBucket(b.id)}
                  className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-muted text-left"
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                  <span className="flex-1 truncate text-xs">{b.name}</span>
                  {isBucketLinked(b.id) && <Check size={11} className="text-primary flex-shrink-0" />}
                </button>
              ))}
            </>
          )}
          {activePriorities.length > 0 && (
            <>
              <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1 mt-1">
                Priorities
              </p>
              {activePriorities.slice(0, 40).map((p) => {
                const bucket = buckets?.find((b) => b.id === p.bucketId)
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => void togglePriority(p.id)}
                    className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-muted text-left"
                  >
                    {bucket && (
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bucket.color }} />
                    )}
                    <span className="flex-1 truncate text-xs">{p.title}</span>
                    {isPriorityLinked(p.id) && <Check size={11} className="text-primary flex-shrink-0" />}
                  </button>
                )
              })}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─── Editor ───────────────────────────────────────────────────────────────────

interface JournalEditorProps {
  entryId: string
  onDelete: () => void
}

export function JournalEditor({ entryId, onDelete }: JournalEditorProps) {
  const entry = useJournalEntry(entryId)
  const links = useJournalLinks(entryId)
  const bucketLinks = useJournalBucketLinks(entryId)
  const buckets = useBuckets()
  const priorities = useAllActivePriorities()
  const updateEntry = useUpdateJournalEntry()
  const deleteEntry = useDeleteJournalEntry()
  const removeJournalLink = useRemoveJournalLink()
  const removeJournalBucketLink = useRemoveJournalBucketLink()

  const [localBody, setLocalBody] = useState('')
  const [preview, setPreview] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (entry) {
      setLocalBody(entry.body)
      setSavedAt(null)
      setPreview(false)
    }
  }, [entry?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function scheduleAutosave(body: string) {
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      await updateEntry.mutateAsync({ id: entryId, data: { body } })
      setSavedAt(new Date())
    }, AUTOSAVE_MS)
  }

  function handleBodyChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setLocalBody(e.target.value)
    scheduleAutosave(e.target.value)
  }

  async function handleTypeChange(type: JournalEntryType) {
    await updateEntry.mutateAsync({ id: entryId, data: { type } })
  }

  async function handleDateChange(e: React.ChangeEvent<HTMLInputElement>) {
    await updateEntry.mutateAsync({ id: entryId, data: { entryDate: e.target.value } })
  }

  async function handleAiExcludableChange(v: boolean) {
    await updateEntry.mutateAsync({ id: entryId, data: { aiExcludable: v } })
  }

  async function selectPrompt(prompt: Prompt) {
    await updateEntry.mutateAsync({ id: entryId, data: { promptUsed: prompt.text } })
    textareaRef.current?.focus()
  }

  async function clearPrompt() {
    await updateEntry.mutateAsync({ id: entryId, data: { promptUsed: undefined } })
  }

  async function handleDelete() {
    clearTimeout(saveTimer.current)
    await deleteEntry.mutateAsync(entryId)
    onDelete()
  }

  useEffect(() => {
    return () => { clearTimeout(saveTimer.current) }
  }, [entryId])

  if (!entry) {
    return <div className="flex items-center justify-center h-full text-sm text-muted-foreground">Loading…</div>
  }

  const prompts = getPromptsForType(entry.type, entryId)

  // Resolve priority link chips
  const priorityChips = (links ?? []).map((link) => {
    const p = priorities?.find((pr) => pr.id === link.priorityId)
    const bucket = p ? buckets?.find((b) => b.id === p.bucketId) : undefined
    return { key: `p:${link.priorityId}`, label: p?.title ?? '…', color: bucket?.color,
      onRemove: () => removeJournalLink.mutate({ journalId: link.journalId, priorityId: link.priorityId }) }
  })

  // Resolve bucket link chips
  const bucketChips = (bucketLinks ?? []).map((link) => {
    const b = buckets?.find((b) => b.id === link.bucketId)
    return { key: `b:${link.bucketId}`, label: b?.name ?? '…', color: b?.color,
      onRemove: () => removeJournalBucketLink.mutate({ journalId: link.journalId, bucketId: link.bucketId }) }
  })

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Meta row */}
      <div className="flex items-center gap-3 mb-3 flex-shrink-0 flex-wrap">
        <input
          type="date"
          defaultValue={entry.entryDate}
          key={entry.id + entry.entryDate}
          onChange={handleDateChange}
          className="text-sm font-medium bg-transparent border-b border-transparent hover:border-border focus:border-ring focus:outline-none"
          aria-label="Entry date"
        />
        <Select value={entry.type} onValueChange={(v) => void handleTypeChange(v as JournalEntryType)}>
          <SelectTrigger className="h-7 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="free">Free write</SelectItem>
            <SelectItem value="discernment">Discernment</SelectItem>
            <SelectItem value="weekly_review">Weekly review</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <span aria-live="polite" className="text-[10px] text-muted-foreground min-w-[5rem] text-right">
            {savedAt ? `Saved ${format(savedAt, 'h:mm a')}` : ''}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 md:h-7 md:w-7 text-muted-foreground"
            onClick={() => setPreview((p) => !p)}
            aria-label={preview ? 'Switch to edit mode' : 'Switch to preview mode'}
            title={preview ? 'Edit' : 'Preview'}
          >
            {preview ? <Pencil size={13} /> : <Eye size={13} />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 md:h-7 md:w-7 text-muted-foreground hover:text-destructive"
            onClick={() => setConfirmDeleteOpen(true)}
            aria-label="Delete entry"
            title="Delete entry"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      </div>

      <Separator className="mb-3 flex-shrink-0" />

      {/* Prompt — selected prompt shown above body; otherwise show chips */}
      {entry.promptUsed ? (
        <div className="flex items-start gap-2 mb-3 px-3 py-2 bg-muted/40 rounded-md flex-shrink-0">
          <p className="flex-1 text-xs text-muted-foreground italic leading-relaxed">{entry.promptUsed}</p>
          <button
            type="button"
            onClick={() => void clearPrompt()}
            className="text-muted-foreground hover:text-foreground mt-0.5 flex-shrink-0"
            aria-label="Remove prompt"
          >
            <X size={11} />
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-3 flex-shrink-0" aria-label="Writing prompts">
          {prompts.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => void selectPrompt(p)}
              className="text-[11px] text-muted-foreground border border-border/60 rounded-full px-2.5 py-0.5 hover:border-ring hover:text-foreground transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {preview ? (
          <div className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-a:text-primary pb-8">
            {localBody.trim() ? (
              <ReactMarkdown>{localBody}</ReactMarkdown>
            ) : (
              <p className="italic text-muted-foreground">Nothing written yet.</p>
            )}
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={localBody}
            onChange={handleBodyChange}
            placeholder="Start writing…"
            className="w-full h-full min-h-[400px] resize-none bg-transparent text-sm leading-relaxed focus:outline-none placeholder:text-muted-foreground font-mono"
            aria-label="Journal entry body"
            spellCheck
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col gap-2 pt-3 mt-3 border-t flex-shrink-0">
        <div className="flex items-center gap-2">
          <Switch
            id={`ai-exclude-${entryId}`}
            checked={entry.aiExcludable}
            onCheckedChange={(v) => void handleAiExcludableChange(v)}
          />
          <Label htmlFor={`ai-exclude-${entryId}`} className="text-xs text-muted-foreground cursor-pointer">
            Exclude from AI coaching
          </Label>
          <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
            {localBody.trim().split(/\s+/).filter(Boolean).length} words
          </span>
        </div>

        {/* Entity links row */}
        <div className="flex flex-wrap items-center gap-1.5">
          {[...bucketChips, ...priorityChips].map(({ key, label, color, onRemove }) => (
            <LinkChip key={key} label={label} color={color} onRemove={onRemove} />
          ))}
          <LinkSelector journalId={entryId} links={links ?? []} bucketLinks={bucketLinks ?? []} />
        </div>
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete this entry?"
        description="This journal entry will be permanently deleted and cannot be recovered."
        confirmLabel="Delete entry"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  )
}
